import _extends from "@babel/runtime/helpers/extends";
import { FormControl, Icon, MenuItem } from "@icg360/ui-toolkit";
import { useInputDebounce } from "@icg360/rex";
import PT from "prop-types";
import React, { Fragment, useEffect, useState, useRef } from "react";
import { chain, invert, uniqBy } from "lodash";
import { toString } from "../../utils/deterministic-string";
import { singleLineAddress } from "../../utils/address/address";
import AddressInput from "../../utils/address/address-input";
import AddressSearch from "../../utils/address/address-search";
import { InputMeta } from "../../utils/address/input-meta";
import setupGoogleMapAPI from "../../utils/address/setup-google-map-api";
import { flattenInputs } from "../utils";
import { Action } from "../components/Action"; // This is for cloning addresses between components

var inMemoryAddresses = {};
var fusionNameToAddressName = {
  streetName: "route",
  addressLine2: "subpremise",
  zipcode: "postalCode",
  zipcodePlus4: "postalCodeSuffix"
};
var addressNameToFusionName = invert(fusionNameToAddressName);

var mapObjectKeysWith = function mapObjectKeysWith(object, mapper) {
  return chain(object).toPairs().map(function (_ref) {
    var key = _ref[0],
        val = _ref[1];
    return [mapper[key] || key, val];
  }).fromPairs().value();
};

AddressFusion.propTypes = {
  disabled: PT.bool,
  groupLabel: PT.string.isRequired,
  isCommercial: PT.bool,
  onChange: PT.func.isRequired,
  value: PT.object
};
AddressFusion.defaultProps = {
  disabled: false,
  isCommercial: false
};
var stylesOverride = /*#__PURE__*/React.createElement("style", {
  dangerouslySetInnerHTML: {
    __html: "\n      .AddressInput {\n        margin: 20px 0 0 15px;\n      }\n    "
  }
});

function AddressFusion(_ref2) {
  var address = _ref2.value,
      isCommercial = _ref2.isCommercial,
      disabled = _ref2.disabled,
      onChange = _ref2.onChange,
      groupLabel = _ref2.groupLabel;

  var _useState = useState(""),
      value = _useState[0],
      setValue = _useState[1];

  var addressInputRef = useRef(null);

  var setAddress = function setAddress(nextAddress) {
    if (nextAddress) {
      setValue("");
      onChange(nextAddress);
    } else {
      onChange();
    }
  };

  var display = address ? singleLineAddress(address) : "";
  var cloneAddresses = uniqBy(Object.entries(inMemoryAddresses).filter(function (_ref3) {
    var key = _ref3[0],
        clone = _ref3[1];
    return clone && key !== groupLabel;
  }).map(function (_ref4) {
    var clone = _ref4[1];
    return {
      clone: clone,
      label: singleLineAddress(clone)
    };
  }), function (_ref5) {
    var label = _ref5.label;
    return label;
  });
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(AddressInput, {
    ref: addressInputRef,
    includeEstablishments: isCommercial,
    isLocked: !!display,
    readOnly: disabled,
    onAddress: setAddress,
    onChange: setValue,
    onEnter: function onEnter(_ref6) {
      var type = _ref6.type,
          highlightIndex = _ref6.highlightIndex;

      if (type === AddressInput.DROPDOWN_TYPES.DEFAULT && highlightIndex >= 0) {
        setAddress(cloneAddresses[highlightIndex].clone);
      }
    },
    defaultMenuItemCount: cloneAddresses.length,
    renderDefault: cloneAddresses.length > 0 ? // eslint-disable-next-line react/display-name, react/prop-types
    function (_ref7) {
      var highlightIndex = _ref7.highlightIndex;
      return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(MenuItem, {
        header: true
      }, "Existing Locations"), cloneAddresses.map(function (_ref8, index) {
        var clone = _ref8.clone,
            label = _ref8.label;
        return /*#__PURE__*/React.createElement(MenuItem, {
          key: label + "-" + index,
          onSelect: function onSelect() {
            setAddress(clone);
            addressInputRef.current.focus();
          },
          className: "AddressInput-suggestion",
          active: highlightIndex === index,
          title: label
        }, /*#__PURE__*/React.createElement(Icon, {
          name: "history",
          className: "icon",
          size: "sm"
        }), " ", label);
      }));
    } : undefined,
    renderFallback: function renderFallback() {
      return /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement(AddressSearch, {
        query: value,
        onAddress: setAddress,
        render: function render(_ref9) {
          var dataMatchesRequest = _ref9.dataMatchesRequest;

          if (!dataMatchesRequest) {
            return null;
          }

          return /*#__PURE__*/React.createElement("p", null, dataMatchesRequest && "Please try a different search.");
        }
      }));
    },
    size: "large",
    value: display || value,
    inputProps: {
      placeholder: isCommercial ? "Search by address or business name..." : "Search by address..."
    }
  }), stylesOverride);
}

Subpremise.propTypes = {
  value: PT.string,
  onChange: PT.func.isRequired,
  disabled: PT.bool
};

function Subpremise(_ref10) {
  var value = _ref10.value,
      onChange = _ref10.onChange,
      disabled = _ref10.disabled;

  var _useInputDebounce = useInputDebounce({
    value: value,
    onChange: onChange,
    wait: 1200
  }),
      internalVal = _useInputDebounce[0],
      setInternalVal = _useInputDebounce[1],
      flush = _useInputDebounce[2];

  return /*#__PURE__*/React.createElement(FormControl, {
    type: "text",
    value: internalVal,
    onChange: setInternalVal,
    onBlur: function onBlur() {
      setTimeout(flush, 100);
    },
    readOnly: disabled
  });
} // Fusion
// name: address
// inputs: display, streetNumber, streetName, addressLine2, city, state, zipcode, zipcodePlus4


export var Address = function Address(_ref11) {
  var inputs = _ref11.inputs,
      onChange = _ref11.onChange,
      onSubmit = _ref11.onSubmit,
      passthroughProps = _ref11.passthroughProps,
      groupLabel = _ref11.groupLabel;
  setupGoogleMapAPI(passthroughProps.googleApiKey);

  var _extractAddressInputs = extractAddressInputs(inputs, passthroughProps.isCommercial),
      addressInputs = _extractAddressInputs.addressInputs,
      validateButton = _extractAddressInputs.validateButton;

  var address = addressInputs.reduce(function (acc, _ref12) {
    var _extends2;

    var fusionName = _ref12.fusionName,
        value = _ref12.value;
    return fusionName ? _extends({}, acc, (_extends2 = {}, _extends2[fusionNameToAddressName[fusionName] || fusionName] = value, _extends2)) : acc;
  }, {});
  var hasValue = chain(address).pick("streetNumber", "streetName", "city", "state", "zipcode", "zipcodePlus4").some().value();
  useEffect(function () {
    inMemoryAddresses[groupLabel] = hasValue ? address : undefined;
    return function () {
      delete inMemoryAddresses[groupLabel];
    };
  }, [toString(address), hasValue, groupLabel]); // We are automatically triggering a validate call so let's
  // wait 5 seconds before we possibly show the button.
  // A user will only need the button to trigger a retry.

  var _useState2 = useState(false),
      isSubmitting = _useState2[0],
      setSubmitting = _useState2[1];

  var submittingTimer = useRef(null);

  var handleSubmit = function handleSubmit() {
    setSubmitting(true);
    onSubmit("validate");
    clearTimeout(submittingTimer.current);
    submittingTimer.current = setTimeout(function () {
      setSubmitting(false);
    }, 5000);
  };

  var handleChange = function handleChange(nextAddress) {
    if (nextAddress && !nextAddress.subpremise) {
      delete nextAddress.subpremise;
    }

    var nextFusionAddress = nextAddress ? mapObjectKeysWith(nextAddress, addressNameToFusionName) : {
      streetNumber: "",
      streetName: "",
      city: "",
      state: "",
      zipcode: "",
      zipcodePlus4: ""
    };
    onChange(nextFusionAddress);

    if (nextAddress) {
      handleSubmit();
    }
  };

  var generateCustomMessage = function generateCustomMessage(_ref13) {
    var errorType = _ref13.errorType,
        inputType = _ref13.inputType;

    if (["minvalue", "maxvalue"].includes(errorType) && inputType === "zipcode") {
      return {
        customMessage: "Property location is not eligible for the current product."
      };
    }
  };

  var validation = addressInputs.reduce(function (_ref14, _ref15) {
    var required = _ref14.required,
        errors = _ref14.errors;
    var isRequired = _ref15.isRequired,
        _ref15$validationErro = _ref15.validationErrors,
        validationErrors = _ref15$validationErro === void 0 ? [] : _ref15$validationErro,
        label = _ref15.label,
        value = _ref15.value,
        inputType = _ref15.type;
    return {
      required: isRequired ? [].concat(required, [label]) : required,
      errors: value ? [].concat(errors, validationErrors.map(function (error) {
        return _extends({}, error, {
          message: label + ": " + error.message
        }, generateCustomMessage({
          errorType: error.type,
          inputType: inputType
        }));
      })) : errors
    };
  }, {
    required: [],
    errors: []
  });
  return /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: 3
  }, /*#__PURE__*/React.createElement(InputMeta, _extends({
    className: "QTCInput--address-fusion"
  }, addressInputs.find(function (_ref16) {
    var fusionName = _ref16.fusionName;
    return fusionName === "streetNumber";
  }), {
    validationErrors: validation.errors,
    value: hasValue ? address : null,
    onChange: handleChange,
    passthroughProps: passthroughProps,
    controlProps: {
      groupLabel: groupLabel,
      isCommercial: passthroughProps.isCommercial
    },
    Control: AddressFusion
  })), !isSubmitting && validateButton && validateButton.value !== "100" && /*#__PURE__*/React.createElement(Action, _extends({}, validateButton, {
    passthroughProps: passthroughProps,
    handleClick: handleSubmit
  }))));
};
Address.propTypes = {
  onChange: PT.func.isRequired,
  onSubmit: PT.func.isRequired,
  inputs: PT.array.isRequired,
  passthroughProps: PT.object,
  groupLabel: PT.string.isRequired
}; // flatten the inputs and seperate addressLine2 and the validate button

function extractAddressInputs(inputs) {
  var flatInputs = flattenInputs(inputs);
  var addressInputs = [];
  var addressLine2 = null;
  var validateButton = null;
  flatInputs.forEach(function (input) {
    if (input.fusionName === "addressLine2") {
      addressLine2 = input;
      return;
    }

    if (input.fusionName === "validate") {
      validateButton = input;
      return;
    }

    addressInputs.push(input);
  });
  return {
    addressInputs: addressInputs,
    addressLine2: addressLine2,
    validateButton: validateButton
  };
}